/**
 * monthLensItems (#1315) — month → WJ LensePanel mapping. Mirrors
 * gutschrift-check's ``monthsToLenseItems`` but groups by Wirtschafts-
 * jahr (Jul→Jun) instead of calendar year, and orders chronologically
 * (a forward liquidity plan reads oldest→newest; gutschrift-check's
 * documents read newest-first). ``LenseItem``/``LenseSubItem`` carry
 * the display string in ``name`` (NOT ``label``); month names are
 * German via ``Intl`` de-DE (whole surface is hardcoded German).
 */
import type { LenseItem, LenseSubItem } from '@hofsync/design-system';

/** 'YYYY-MM' → German month name, e.g. '2026-07' → 'Juli'. */
export function formatMonthName(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long' });
}

/** Calendar year/month → the Wirtschaftsjahr's starting calendar year.
 *  Exported so the category-grouped view (#1617) labels its WJ column identically. */
export function wjStartYear(ym: string, wjStartMonth: number): number {
  const [y, m] = ym.split('-').map(Number);
  return m >= wjStartMonth ? y : y - 1;
}

/** Starting year 2026 → 'WS 26/27'. Exported for the #1617 WJ column header. */
export function wjLabel(startYear: number): string {
  const a = String(startYear).slice(2);
  const b = String(startYear + 1).slice(2);
  return `WS ${a}/${b}`;
}

export function monthsToWjLensItems(
  availableMonths: string[],
  wjStartMonth: number,
  countsByMonth: Record<string, number> = {},
): LenseItem[] {
  // Chronological ascending — a forward plan reads oldest→newest.
  const monthsAsc = [...availableMonths].sort();

  const byWj = new Map<number, string[]>();
  for (const ym of monthsAsc) {
    const start = wjStartYear(ym, wjStartMonth);
    const bucket = byWj.get(start) ?? [];
    bucket.push(ym);
    byWj.set(start, bucket);
  }

  return [...byWj.entries()]
    .sort(([a], [b]) => a - b)
    .map(([startYear, yms]) => {
      const children: LenseSubItem[] = yms.map((ym) => ({
        id: ym,
        name: formatMonthName(ym),
        count: countsByMonth[ym] ?? 0,
      }));
      return {
        id: `wj-${startYear}`,
        name: wjLabel(startYear),
        children,
      };
    });
}
